/**
 * PlayerStart - simple actor marking where players should spawn.
 * Provides a transform for GameMode to determine initial pawn placement.
 */
import * as THREE from 'three';

import { EngineClass } from '../systems/ClassRegistry.js';

import { Actor } from './Actor.js';


@EngineClass('PlayerStart')
export class PlayerStart extends Actor {
  /** Editor-only debug arrow. Held so we can dispose it on removal. */
  private editorArrow: THREE.ArrowHelper | null = null;

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public override onEditorAddToWorld(): void {
    super.onEditorAddToWorld();
    const arrowHelper = new THREE.ArrowHelper(
      new THREE.Vector3(0, 0, -1),
      new THREE.Vector3(0, 0.02, 0),
      2,
      0x00ffff,
    );
    arrowHelper.setTransient(true);

    // `THREE.ArrowHelper` constructs `line` and `cone` against module-level
    // *shared* singleton geometries (`_lineGeometry` / `_coneGeometry` in
    // three.core). The renderer registers a `dispose` listener on those
    // geometries when it builds a `RenderObject` for each Line/Mesh, and
    // that listener captures the Line/Mesh — and through `parent` chains,
    // this entire actor. Because the shared geometries are never disposed
    // (they live forever in three's closure) the listener never fires and
    // every removed PlayerStart leaks one Line + Mesh + ArrowHelper +
    // root SceneComponent + actor instance.
    //
    // Owning a per-instance clone of each geometry lets us call
    // `dispose()` on `onEditorRemoveFromWorld`, which fires the renderer's
    // listener and releases the cached `RenderObject`.
    arrowHelper.line.geometry = arrowHelper.line.geometry.clone();
    arrowHelper.cone.geometry = arrowHelper.cone.geometry.clone();

    this.rootComponent?.add(arrowHelper);
    this.editorArrow = arrowHelper;
  }

  public override onEditorRemoveFromWorld(): void {
    if (this.editorArrow) {
      this.editorArrow.removeFromParent();
      // `ArrowHelper.dispose()` calls dispose on `line.geometry`,
      // `line.material`, `cone.geometry` and `cone.material`. Because we
      // replaced the shared singleton geometries with per-instance clones in
      // `onEditorAddToWorld`, those dispose calls now affect only this
      // helper's resources and trigger `RenderObject.dispose()` in the
      // WebGPU renderer, which is what actually breaks the listener-based
      // retention chain to the rest of the actor subtree.
      this.editorArrow.dispose();
      this.editorArrow = null;
    }
    super.onEditorRemoveFromWorld();
  }

  public override getEditorClassIcon(): string | null {
    return 'Icon_PlayerStart';
  }
}
